import React, { useEffect, useMemo, useState } from 'react';
import { Form, Grid } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import BBuildingContactPersonListContainer from './BBuildingContactPersonListContainer';
import CPHazardsContainer from '../ChemicalProduct/ChemicalProductDetails/GeneralInformation/CPHazardsContainer';
import BRisksContainer from './BRisksContainer';
import ReadOnlyMapContainer from './ReadOnlyMapContainer';
import BImportantInformationContainer from './BImportantInformationContainer';
import { compose } from 'redux';
import connect from 'react-redux/es/connect/connect';
import { reportsActions } from '../../behavior/reports';
import { GET_BUILDINGS_REPORT_PATH } from '../../behavior/reports/types';
import { useTitleDispatch } from '../TitleContextProvider';
import BQRLauncher from './BQRLauncher';
import BInventoryRecordsContactPersonListContainer from './BInventoryRecordsContactPersonListContainer';
import BBuildingDetailsContainer from './BBuildingDetailsContainer';
import { LOGO_IMAGE_PATH } from '../../constants';
import i18n from 'i18next';
import { gridTemplatesOptions } from '../../objects/StickerGridTemplate';
import DownloadStickersModal from '../common/DownloadStickersModal';
import { identityFunction } from '../../utils';
import { locationsActions } from '../../behavior/locations';

const summaryTheme = createTheme({
  typography: { fontFamily: 'inherit' },
  palette: { mode: 'light', primary: { main: '#0c0c0c' } },
  shape: { borderRadius: 4 },
});

const BuildingSummaryForm = ({
  getBuildingReport,
  formikProps,
  detailContainerProps,
  t,
  isReportLoading,
}) => {
  const props = { ...formikProps, ...detailContainerProps };
  const { values } = formikProps;
  const { buildingNum } = detailContainerProps;
  const pageTitle = t('building.summary.page.building.summary.container.title', {
    locationInfo: values.location.locationInfoWithAcronym,
  });
  const dispatch = useTitleDispatch();
  const [mapImage, setMapImage] = useState(null);

  useEffect(() => {
    dispatch({
      pageTitle,
    });
  }, [i18n.language]);

  const downloadQRStickersButton = useMemo(
    () => setOpenModal =>
      (
        <Button
          variant="outlined"
          size="small"
          startIcon={<QrCode2Icon />}
          onClick={() => setOpenModal(true)}
          sx={{ textTransform: 'none', fontFamily: 'inherit' }}
        >
          {t('download.stickers.button.label')}
        </Button>
      ),
    [t]
  );

  const selectedItemsSupplier = useMemo(
    () => () => [values?.location].map(building => building.id),
    [values?.location]
  );

  const itemsLabelsSupplier = useMemo(
    () => () =>
      Object.assign(
        {},
        ...[values?.location].map(building => ({
          [building.id]: `BUILDING: ${building?.locationInfoWithAcronym}`,
        }))
      ),
    [values?.location]
  );

  const urlTemplateMapper = useMemo(() => ignored => `${window.location}`, [window.location]);

  const getButtons = () => {
    const pdfDisabled = isReportLoading || (!mapImage && !!window.CERNMap);
    return (
      <Stack direction="row" spacing={1}>
        <Button
          key="export.pdf.button"
          variant="outlined"
          size="small"
          startIcon={
            isReportLoading ? (
              <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
            ) : (
              <PictureAsPdfIcon />
            )
          }
          onClick={() => getBuildingReport([{ mapImage, buildingNum }])}
          disabled={pdfDisabled}
          sx={{ textTransform: 'none', fontFamily: 'inherit' }}
        >
          {t('details.page.export.pdf.button.tooltip')}
        </Button>
        <DownloadStickersModal
          key="export.qr.stickers.button"
          downloadStickersTriggerComponent={downloadQRStickersButton}
          createPDFActionMapper={locationsActions.createBuildingsQRStickersPdf}
          selectedItemsSupplier={selectedItemsSupplier}
          itemsLabelsSupplier={itemsLabelsSupplier}
          itemsLabelResultMapper={identityFunction}
          urlTemplateMapper={urlTemplateMapper}
          gridTemplateCreatorOptions={gridTemplatesOptions}
          downloadFileName={`building-${buildingNum}-qr-sticker.pdf`.replace(/\s+/g, '')}
        />
      </Stack>
    );
  };

  return (
    <Form className="buildingSummaryForm smallScreenNoMargin">
      <ThemeProvider theme={summaryTheme}>
        <Box
          className="buildingSummaryTitle"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            columnGap: 1,
            alignItems: 'start',
            px: 0.25,
            pt: 0.25,
          }}
        >
          <Box sx={{ gridColumn: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 1,
                rowGap: 0.25,
                flexWrap: 'wrap',
                mb: 0.25,
              }}
            >
              <Box
                component="h1"
                sx={{
                  m: 0,
                  flex: '1 1 260px',
                  minWidth: 0,
                  fontSize: { xs: 17, md: 19 },
                  fontWeight: 500,
                  lineHeight: 1.2,
                  letterSpacing: '-0.005em',
                  color: '#0c0c0c',
                }}
              >
                {pageTitle}
              </Box>
              <Box sx={{ flexShrink: 0 }}>{getButtons()}</Box>
            </Box>
            <BBuildingDetailsContainer {...props} />
            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <BRisksContainer {...props} />
            </Box>
          </Box>
          <Box sx={{ gridColumn: 2 }}>
            <BQRLauncher
              title={pageTitle}
              smallSize={96}
              largeSize={280}
              logoImagePath={LOGO_IMAGE_PATH}
            />
          </Box>
        </Box>
      </ThemeProvider>
      <Grid stackable columns={2} className="buildingSummaryDetailsGrid smallScreenReducePadding">
        <Grid.Column className="detailsGridCol">
          <Grid className="">
            <Grid.Row columns={2}>
              <Grid.Column width={16}>
                <CPHazardsContainer
                  {...props}
                  hazards={values.hazards || []}
                  arrayName="hazards"
                  disabled={true}
                  showAllHazards={false}
                />
              </Grid.Column>
              <Grid.Column
                computer={8}
                mobile={16}
                only="computer mobile"
                className="detailsGridCol buildingContactPersonList"
              >
                <BBuildingContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
              <Grid.Column
                computer={8}
                mobile={16}
                only="computer mobile"
                className="detailsGridCol inventoryRecordsContactPersonList"
              >
                <BInventoryRecordsContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
              <Grid.Column width={16}>
                <BImportantInformationContainer {...props} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>

        <Grid.Column className="detailsGridCol">
          <Grid className="">
            <Grid.Row>
              <Grid.Column width={16}>
                <ReadOnlyMapContainer location={values.location} setMapImage={setMapImage} />
              </Grid.Column>
              <Grid.Column
                width={16}
                only="tablet"
                className="detailsGridCol buildingContactPersonList"
              >
                <BBuildingContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
              <Grid.Column
                width={16}
                only="tablet"
                className="detailsGridCol inventoryRecordsContactPersonList"
              >
                <BInventoryRecordsContactPersonListContainer {...props} disabled={true} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid>
    </Form>
  );
};

const mapStateToProps = state => ({
  isReportLoading: state.reports.isReportLoading,
});

const mapDispatchToProps = dispatch => ({
  getBuildingReport: locReportReqDtoList =>
    dispatch(reportsActions.getReport(GET_BUILDINGS_REPORT_PATH, null, locReportReqDtoList)),
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(BuildingSummaryForm);
